// Map markers copied from the Figma component "tam/map-marker" (frames 02, 03, 02b).
// Each pin is a square SVG; `size` is its CSS size including the drop-shadow margin.

export interface Pin {
  id: string;
  size: number;
  svg: string;
}

const shadow = (id: string, dy: number, blur: number, alpha: number) =>
  `<filter id="${id}" x="-50%" y="-50%" width="200%" height="200%" color-interpolation-filters="sRGB">
    <feDropShadow dx="0" dy="${dy}" stdDeviation="${blur}" flood-color="rgb(13,8,51)" flood-opacity="${alpha}"/></filter>`;

const doc = (size: number, body: string, defs = "") =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none"><defs>${defs}</defs>${body}</svg>`;

// icon/bike, drawn for a 32 px marker whose top-left is at (3, 2).
const bikeGlyph = (stroke: string) =>
  `<g stroke="${stroke}" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="13.58" cy="20.92" r="3"/><circle cx="24.42" cy="20.92" r="3"/>
    <path d="M22.5 15.5H16.75L13.58 20.92M16.75 15.5L18.67 20.92L22.5 15.5M22.5 15.5L24.42 20.92M22.5 15.5L21.83 13.42H23.67M15.67 13.83H18"/></g>`;

// icon/parking, drawn for a 28 px marker whose top-left is at (3, 2).
const parkingGlyph = `<path fill="#fff" d="M17.75 9.25H12.5V22.75H15.5V18.25H17.75C20.23 18.25 22.25 16.23 22.25 13.75C22.25 11.27 20.23 9.25 17.75 9.25ZM17.9 15.25H15.5V12.25H17.9C18.73 12.25 19.4 12.93 19.4 13.75C19.4 14.58 18.73 15.25 17.9 15.25Z"/>`;

const parkingMarker = `<g filter="url(#s)">
    <rect x="3" y="2" width="28" height="28" rx="7" fill="#321ED3"/>
    <rect x="4" y="3" width="26" height="26" rx="6" stroke="#fff" stroke-width="2"/>${parkingGlyph}</g>`;

// "Icon/Scooter" from the Pony app flows (the TaM screens have no scooter icon), 20 × 18 box, filled.
export const SCOOTER_PATH =
  "M13.33 0.06C13.47 0.1 13.58 0.18 13.68 0.27L13.7 0.29L13.74 0.33C13.8 0.4 13.86 0.47 13.9 0.56C13.93 0.63 13.96 0.7 13.97 0.77L14.49 3L15 3C15.55 3 16 3.45 16 4C16 4.51 15.61 4.94 15.12 4.99L15 5L14.95 5L16.1 10L16.2 10.01C18.32 10.11 20 11.86 20 14C20 16.21 18.21 18 16 18C14.14 18 12.57 16.73 12.13 15L4 15C3.45 15 3 14.55 3 14C3 13.49 3.39 13.06 3.88 13.01L4 13H14.74L12.2 2L9 2C8.45 2 8 1.55 8 1C8 0.49 8.39 0.06 8.88 0.01L9 0L13 0C13.12 0 13.23 0.02 13.33 0.06ZM6.83 11.17C7.22 11.56 7.22 12.2 6.83 12.59C6.44 12.98 5.8 12.98 5.41 12.59C4.63 11.81 3.37 11.81 2.59 12.59C1.8 13.37 1.8 14.63 2.59 15.41C3.33 16.16 4.52 16.19 5.3 15.52L5.41 15.41C5.8 15.02 6.44 15.02 6.83 15.41C7.22 15.81 7.22 16.44 6.83 16.83C5.27 18.39 2.73 18.39 1.17 16.83C-0.39 15.27 -0.39 12.73 1.17 11.17C2.73 9.61 5.27 9.61 6.83 11.17ZM16.58 12.09L16.97 13.78C17.11 14.36 16.7 14.93 16.12 14.99L16 15L14.27 15C14.61 15.6 15.26 16 16 16C17.1 16 18 15.1 18 14C18 13.1 17.4 12.34 16.58 12.09Z";

// Vehicle markers: 32 px disc at rest, 40 px when selected, for every type; glyph sizes in GLYPH. A glyph's visual extent (stroke included) fits its square and is centred
// on the disc. Same canvas and centre for every type, so all pins anchor identically on the map.
const REST = { canvas: 38, cx: 19, cy: 18, disc: 32 } as const;
const SELECTED = { canvas: 62, cx: 31, cy: 28, disc: 40 } as const;
/** Glyph size [rest, selected]. The filled scooter glyph is drawn smaller to weigh like the stroked bike. */
const GLYPH = { bike: [16, 20], scooter: [14, 18] } as const;

const disc = (cx: number, cy: number, d: number, fill: string, ring: string, ringW: number) => {
  const r = d / 2;
  const inset = ringW / 2;
  return `<rect x="${cx - r}" y="${cy - r}" width="${d}" height="${d}" rx="${r}" fill="${fill}"/>
      <rect x="${cx - r + inset}" y="${cy - r + inset}" width="${d - ringW}" height="${d - ringW}" rx="${r - inset}" stroke="${ring}" stroke-width="${ringW}"/>`;
};

// Visual box of the bike glyph in its own coordinates, stroke (1.7) included.
const BIKE_BOX = { x0: 9.73, y0: 12.57, x1: 28.27, y1: 24.77 };
/** Bike glyph scaled so its larger side equals `size`, centred on (cx, cy). */
const bikeGlyphFit = (color: string, cx: number, cy: number, size: number) => {
  const w = BIKE_BOX.x1 - BIKE_BOX.x0;
  const h = BIKE_BOX.y1 - BIKE_BOX.y0;
  const k = size / Math.max(w, h);
  const tx = cx - (BIKE_BOX.x0 + w / 2) * k;
  const ty = cy - (BIKE_BOX.y0 + h / 2) * k;
  return `<g transform="translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${k.toFixed(4)})">${bikeGlyph(color)}</g>`;
};

/** Scooter glyph (20 × 18, filled) scaled so its width equals `size`, centred on (cx, cy). */
export const scooterGlyph = (color: string, cx: number, cy: number, size: number) => {
  const k = size / 20;
  return `<path fill="${color}" fill-rule="evenodd" transform="translate(${(cx - 10 * k).toFixed(2)} ${(cy - 9 * k).toFixed(2)}) scale(${k.toFixed(3)})" d="${SCOOTER_PATH}"/>`;
};

type Glyph = (color: string, cx: number, cy: number, size: number) => string;

const restPin = (id: string, glyph: Glyph, size: number): Pin => ({
  id,
  size: REST.canvas,
  svg: doc(
    REST.canvas,
    `<g filter="url(#s)">${disc(REST.cx, REST.cy, REST.disc, "#321E64", "#fff", 2)}${glyph("#fff", REST.cx, REST.cy, size)}</g>`,
    shadow("s", 1, 1.5, 0.3),
  ),
});

const selectedPin = (id: string, glyph: Glyph, size: number): Pin => ({
  id,
  size: SELECTED.canvas,
  svg: doc(
    SELECTED.canvas,
    `<g filter="url(#s)">${disc(SELECTED.cx, SELECTED.cy, SELECTED.disc, "#00FFFF", "#321E64", 3)}${glyph("#321E64", SELECTED.cx, SELECTED.cy, size)}</g>`,
    shadow("s", 3, 4, 0.35),
  ),
});

export const PIN_VEHICLE = restPin("pin-vehicle", bikeGlyphFit, GLYPH.bike[0]);
export const PIN_VEHICLE_SELECTED = selectedPin(
  "pin-vehicle-selected",
  bikeGlyphFit,
  GLYPH.bike[1],
);
export const PIN_SCOOTER = restPin("pin-scooter", scooterGlyph, GLYPH.scooter[0]);
export const PIN_SCOOTER_SELECTED = selectedPin(
  "pin-scooter-selected",
  scooterGlyph,
  GLYPH.scooter[1],
);

// Figma P2: the rider's pin while the ride is paused, with a 24 px violet pause badge (2 px white
// stroke) on its top-right. Canvas grown by 4 px on each side so the badge fits and the anchor
// (canvas centre) stays where the selected pin's is.
const PAUSED = { canvas: SELECTED.canvas + 8, cx: SELECTED.cx + 4, cy: SELECTED.cy + 4 } as const;
const pausedPin = (id: string, glyph: Glyph, size: number): Pin => {
  const bx = PAUSED.cx + SELECTED.disc / 2 - 9;
  const by = PAUSED.cy - SELECTED.disc / 2 - 7;
  return {
    id,
    size: PAUSED.canvas,
    svg: doc(
      PAUSED.canvas,
      `<g filter="url(#s)">${disc(PAUSED.cx, PAUSED.cy, SELECTED.disc, "#00FFFF", "#321E64", 3)}${glyph("#321E64", PAUSED.cx, PAUSED.cy, size)}</g>
      <circle cx="${bx + 12}" cy="${by + 12}" r="11" fill="#6D3E91" stroke="#fff" stroke-width="2"/>
      <path fill="#fff" transform="translate(${bx + 5} ${by + 5})" d="M3.5 11.0833H5.83333V2.91667H3.5V11.0833ZM8.16667 2.91667V11.0833H10.5V2.91667H8.16667Z"/>`,
      shadow("s", 3, 4, 0.35),
    ),
  };
};

export const PIN_VEHICLE_PAUSED = pausedPin("pin-vehicle-paused", bikeGlyphFit, GLYPH.bike[1]);
export const PIN_SCOOTER_PAUSED = pausedPin("pin-scooter-paused", scooterGlyph, GLYPH.scooter[1]);

// Figma tam/map-marker "Vehicle booked" (55:9383): the selected pin with badge/booked, a 20 px indigo
// disc (2 px white border) and a white 14 px clock, overhanging its top-right. Same grown canvas as
// the paused pin, so it anchors exactly like the selected one.
const CLOCK_14 =
  "M6.99417 1.16667C3.77417 1.16667 1.16667 3.78 1.16667 7C1.16667 10.22 3.77417 12.8333 6.99417 12.8333C10.22 12.8333 12.8333 10.22 12.8333 7C12.8333 3.78 10.22 1.16667 6.99417 1.16667ZM7 11.6667C4.42167 11.6667 2.33333 9.57833 2.33333 7C2.33333 4.42167 4.42167 2.33333 7 2.33333C9.57833 2.33333 11.6667 4.42167 11.6667 7C11.6667 9.57833 9.57833 11.6667 7 11.6667ZM7.29167 4.08333H6.41667V7.58333L9.47917 9.42083L9.91667 8.70333L7.29167 7.14583V4.08333Z";
const bookedPin = (id: string, glyph: Glyph, size: number): Pin => {
  const r = SELECTED.disc / 2;
  const bx = PAUSED.cx + r - 6;
  const by = PAUSED.cy - r + 4;
  return {
    id,
    size: PAUSED.canvas,
    svg: doc(
      PAUSED.canvas,
      `<g filter="url(#s)">${disc(PAUSED.cx, PAUSED.cy, SELECTED.disc, "#00FFFF", "#321E64", 3)}${glyph("#321E64", PAUSED.cx, PAUSED.cy, size)}</g>
      <circle cx="${bx}" cy="${by}" r="9" fill="#321E64" stroke="#fff" stroke-width="2"/>
      <path fill="#fff" transform="translate(${bx - 7} ${by - 7})" d="${CLOCK_14}"/>`,
      shadow("s", 3, 4, 0.35),
    ),
  };
};

export const PIN_VEHICLE_BOOKED = bookedPin("pin-vehicle-booked", bikeGlyphFit, GLYPH.bike[1]);
export const PIN_SCOOTER_BOOKED = bookedPin("pin-scooter-booked", scooterGlyph, GLYPH.scooter[1]);

export const PIN_PARKING: Pin = {
  id: "pin-parking",
  size: 34,
  svg: doc(34, parkingMarker, shadow("s", 1, 1.5, 0.3)),
};

export const PIN_PARKING_SELECTED: Pin = {
  id: "pin-parking-selected",
  size: 60,
  // Figma 02b: 60 px #321ED3 halo at 20 % behind the regular parking marker.
  svg: doc(
    60,
    `<circle cx="30" cy="30" r="30" fill="#321ED3" opacity="0.2"/><g transform="translate(13 14)">${parkingMarker}</g>`,
    shadow("s", 1, 1.5, 0.3),
  ),
};

// Figma 287:14374, parking density by zoom: a 14 px lilac square outlined in dark blue at mid zoom,
// an 8 px dark blue square when far. 2 px canvas margin for the shadow / border.
export const PIN_PARKING_MID: Pin = {
  id: "pin-parking-mid",
  size: 20,
  svg: doc(
    20,
    `<g filter="url(#s)"><rect x="3" y="2" width="14" height="14" rx="3.5" fill="#F3E5F5"/>
      <rect x="4.25" y="3.25" width="11.5" height="11.5" rx="2.25" stroke="#321ED3" stroke-width="2.5"/></g>`,
    shadow("s", 1, 1.5, 0.3),
  ),
};

export const PIN_PARKING_LOW: Pin = {
  id: "pin-parking-low",
  size: 10,
  svg: doc(
    10,
    `<rect x="1" y="1" width="8" height="8" rx="2" fill="#321ED3"/>
      <rect x="1.5" y="1.5" width="7" height="7" rx="1.5" stroke="#fff"/>`,
  ),
};

/**
 * Parking marker for a zoom level, same bands as the Pony rider app (zones_config_v2.parking default
 * zoom levels 10 / 15 / 16): none below 10, the small square up to 15, the outlined square up to 16,
 * the P beyond.
 */
export const PARKING_ZOOMS = { low: 10, mid: 15, full: 16 } as const;
export function parkingPinFor(zoom: number): string | undefined {
  if (zoom < PARKING_ZOOMS.low) return undefined;
  if (zoom < PARKING_ZOOMS.mid) return PIN_PARKING_LOW.id;
  if (zoom < PARKING_ZOOMS.full) return PIN_PARKING_MID.id;
  return PIN_PARKING.id;
}

export const PIN_USER: Pin = {
  id: "pin-user",
  size: 64,
  svg: doc(
    64,
    `<circle cx="32" cy="32" r="32" fill="#1E90FF" opacity="0.18"/><circle cx="32" cy="32" r="8.5" fill="#1E90FF" stroke="#fff" stroke-width="3"/>`,
  ),
};

export const ALL_PINS = [
  PIN_VEHICLE,
  PIN_VEHICLE_SELECTED,
  PIN_SCOOTER,
  PIN_SCOOTER_SELECTED,
  PIN_VEHICLE_PAUSED,
  PIN_SCOOTER_PAUSED,
  PIN_VEHICLE_BOOKED,
  PIN_SCOOTER_BOOKED,
  PIN_PARKING,
  PIN_PARKING_MID,
  PIN_PARKING_LOW,
  PIN_PARKING_SELECTED,
  PIN_USER,
];

export const pinUrl = (pin: Pin, scale = 1) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    scale === 1
      ? pin.svg
      : pin.svg.replace(
          `width="${pin.size}" height="${pin.size}"`,
          `width="${pin.size * scale}" height="${pin.size * scale}"`,
        ),
  )}`;
